import { useEffect, useState } from 'react';
import { Plus, Trash2, Save, X } from 'lucide-react';
import { knowledgeAPI } from '../services/api.js';

// Inline Knowledge-article CRUD. Articles are injected (within budget) into
// the AI's system prompt so it can answer factually about THIS tenant.
//
// Optional `campaignId` prop scopes the editor to one campaign: it lists ONLY
// that campaign's own articles (scope=only) and stamps new articles with the
// campaignId so they're used only on that campaign's calls. Without the prop it
// behaves exactly as before — the tenant-wide shared library.

const EMPTY = { title: '', content: '', category: '' };

export default function KnowledgeEditor({ campaignId = null }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState({});
  const [creating, setCreating] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await knowledgeAPI.list(campaignId ? { campaignId, only: true } : undefined);
      setItems(r?.data || []);
    } catch (err) {
      if (err.status !== 404) setError(err.message || 'Failed to load knowledge.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [campaignId]);

  const startEdit = (item) =>
    setEditing((e) => ({ ...e, [item.id]: { title: item.title, content: item.content, category: item.category || '' } }));
  const cancelEdit = (id) =>
    setEditing((e) => { const n = { ...e }; delete n[id]; return n; });

  const saveEdit = async (id) => {
    const draft = editing[id];
    if (!draft) return;
    try {
      const r = await knowledgeAPI.update(id, draft);
      setItems((list) => list.map((i) => (i.id === id ? r.data : i)));
      cancelEdit(id);
    } catch (err) {
      setError(err.message || 'Save failed.');
    }
  };

  const remove = async (id) => {
    if (!confirm('Remove this article from your AI?')) return;
    try {
      await knowledgeAPI.remove(id);
      setItems((list) => list.filter((i) => i.id !== id));
    } catch (err) {
      setError(err.message || 'Delete failed.');
    }
  };

  const create = async () => {
    if (!creating.title.trim() || !creating.content.trim()) return;
    try {
      const r = await knowledgeAPI.create({
        title: creating.title.trim(),
        content: creating.content.trim(),
        category: creating.category.trim() || undefined,
        ...(campaignId ? { campaignId } : {})
      });
      setItems((list) => [r.data, ...list]);
      setCreating(EMPTY);
      setAdding(false);
    } catch (err) {
      setError(err.message || 'Create failed.');
    }
  };

  return (
    <section className="card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl text-stone-100">
            {campaignId ? 'Campaign knowledge articles' : 'Knowledge articles'}
          </h2>
          <p className="text-sm text-stone-400 mt-1">
            {campaignId
              ? 'Facts used ONLY on this campaign\'s calls — layered on top of the tenant-wide library. Write them as paragraphs the AI can rephrase.'
              : 'Background facts your AI can draw from — products, pricing, policies, hours. Write them as paragraphs the AI can rephrase, not bullet lists.'}
          </p>
        </div>
        {!adding && (
          <button type="button" className="btn-ghost" onClick={() => setAdding(true)}>
            <Plus size={14} /> Add article
          </button>
        )}
      </div>

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}

      {adding && (
        <div className="mb-4 rounded-xl border border-alfred-gold/30 p-4 space-y-3">
          <input
            className="field-input"
            placeholder="Title (e.g. How our pricing tiers work)"
            value={creating.title}
            onChange={(e) => setCreating({ ...creating, title: e.target.value })}
            autoFocus
          />
          <textarea
            className="field-input min-h-[140px]"
            placeholder="Body — a paragraph the AI can paraphrase. ~50–250 words is the sweet spot."
            value={creating.content}
            onChange={(e) => setCreating({ ...creating, content: e.target.value })}
          />
          <input
            className="field-input"
            placeholder="Category (optional, e.g. Pricing)"
            value={creating.category}
            onChange={(e) => setCreating({ ...creating, category: e.target.value })}
          />
          <div className="flex gap-2">
            <button type="button" className="btn-primary" onClick={create}>
              <Save size={14} /> Save
            </button>
            <button type="button" className="btn-ghost" onClick={() => { setAdding(false); setCreating(EMPTY); }}>
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-stone-500">No articles yet. Add one to get started.</p>
      ) : (
        <ul className="divide-y divide-stone-900">
          {items.map((k) => {
            const draft = editing[k.id];
            if (draft) {
              return (
                <li key={k.id} className="py-4 space-y-3">
                  <input
                    className="field-input"
                    value={draft.title}
                    onChange={(e) => setEditing((s) => ({ ...s, [k.id]: { ...s[k.id], title: e.target.value } }))}
                  />
                  <textarea
                    className="field-input min-h-[140px]"
                    value={draft.content}
                    onChange={(e) => setEditing((s) => ({ ...s, [k.id]: { ...s[k.id], content: e.target.value } }))}
                  />
                  <input
                    className="field-input"
                    placeholder="Category"
                    value={draft.category}
                    onChange={(e) => setEditing((s) => ({ ...s, [k.id]: { ...s[k.id], category: e.target.value } }))}
                  />
                  <div className="flex gap-2">
                    <button type="button" className="btn-primary" onClick={() => saveEdit(k.id)}>
                      <Save size={14} /> Save
                    </button>
                    <button type="button" className="btn-ghost" onClick={() => cancelEdit(k.id)}>
                      <X size={14} /> Cancel
                    </button>
                  </div>
                </li>
              );
            }
            return (
              <li key={k.id} className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-stone-100 font-medium">{k.title}</span>
                      {k.category && (
                        <span className="badge bg-stone-800 text-stone-300 border border-stone-700 text-[10px]">
                          {k.category}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-stone-300 leading-relaxed whitespace-pre-wrap">
                      {k.content}
                    </p>
                  </div>
                  <div className="shrink-0 flex items-center gap-1">
                    <button type="button" className="btn-ghost" onClick={() => startEdit(k)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn-ghost text-red-300 hover:text-red-200"
                      onClick={() => remove(k.id)}
                      aria-label="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
